import { isSuccess } from '@/context/services/type';
import {
  CommonError,
  usePostApiJsApPayeesInvitationsMails as useBasePostApiJsApPayeesInvitationsMails,
  usePostApiJsApPayeesInvitationsPdfs as useBasePostApiJsApPayeesInvitationsPdfs,
  useGetApPayeeContactInformPdfTemplateSuspense,
  useGetApPayeeContactInformTemplateSuspense,
  useGetApPayeesBulkInvitationsAvailabilityHook,
  useGetApPayeesMacro,
} from 'ap-openapi';
import { useCallback } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { SearchApPayeeQuery, useSearchApPayeesHook } from 'ap-openapi';

type TPostApiJsApPayeesInvitationsPdfsMutateAsync = ReturnType<
  typeof useBasePostApiJsApPayeesInvitationsPdfs
>['mutateAsync'];
type PostApiJsApPayeesInvitationsPdfsMutateArgs =
  Parameters<TPostApiJsApPayeesInvitationsPdfsMutateAsync>['0'];
type PostApiJsApPayeesInvitationsPdfsMutateOption =
  Parameters<TPostApiJsApPayeesInvitationsPdfsMutateAsync>['1'];

type TPostApiJsApPayeesInvitationsMailsMutateAsync = ReturnType<
  typeof useBasePostApiJsApPayeesInvitationsMails
>['mutateAsync'];
type PostApiJsApPayeesInvitationsMailsMutateArgs =
  Parameters<TPostApiJsApPayeesInvitationsMailsMutateAsync>['0'];
type PostApiJsApPayeesInvitationsMailsMutateOption =
  Parameters<TPostApiJsApPayeesInvitationsMailsMutateAsync>['1'];

export const useGetApPayeeContactInformPdfTemplate = () => {
  const { data, error } = useGetApPayeeContactInformPdfTemplateSuspense();
  if (error) throw error;
  return data;
};

export const usePostApiJsApPayeesInvitationsPdfs = () => {
  const { mutateAsync: _mutateAsync, isPending } =
    useBasePostApiJsApPayeesInvitationsPdfs();
  const mutateAsync = useCallback(
    async (
      args: PostApiJsApPayeesInvitationsPdfsMutateArgs,
      options?: PostApiJsApPayeesInvitationsPdfsMutateOption
    ) => {
      const resp = await _mutateAsync(args, options);
      if (isSuccess(resp.status)) {
        return {
          status: 'success',
          data: resp.data,
          redirectUrl: resp?.data?.redirect_url,
        };
      }
      return {
        status: 'error',
        errors: resp.data ?? {},
      };
    },
    [_mutateAsync]
  );

  return { mutateAsync, isPending };
};

export const useGetApPayeesMacroList = (apPayeeId: string) => {
  const { data, error, isFetching } = useGetApPayeesMacro(apPayeeId, {
    query: {
      enabled: !!apPayeeId,
    },
  });

  if (error) return { error, isFetching };

  if (isSuccess(data?.status)) {
    return {
      data,
      isFetching,
    };
  } else {
    return {
      isFetching,
      error: {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        ...((data?.data ?? {}) as CommonError),
      },
    };
  }
};

export const useGetApPayeesInfinite = (payeeQuery: SearchApPayeeQuery) => {
  /**
   *  Using the useSearchApPayeesHook here because  it is a post request and
   *  the useInfiniteQuery is not generate from ovral so i use it manually here.
   */
  const fetchSearchApPayees = useSearchApPayeesHook();
  const {
    data: apiData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ['searchApPayees', payeeQuery],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchSearchApPayees({
        query: payeeQuery,
        pagination: {
          page: pageParam,
          per_page: 25,
        },
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.data.pagination?.has_next_page
        ? (lastPage.data.pagination?.page ?? 0) + 1
        : undefined;
    },
    initialPageParam: 1,
    enabled: !!payeeQuery,
  });

  return {
    apiData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  };
};

export const useGetApPayeeContactInformTemplate = () => {
  const { data, error } = useGetApPayeeContactInformTemplateSuspense();
  if (error) throw error;
  return data;
};

export const usePostApiJsApPayeesInvitationsMails = () => {
  const { mutateAsync: _mutateAsync, isPending } =
    useBasePostApiJsApPayeesInvitationsMails();

  const mutateAsync = useCallback(
    async (
      args: PostApiJsApPayeesInvitationsMailsMutateArgs,
      options?: PostApiJsApPayeesInvitationsMailsMutateOption
    ) => {
      const resp = await _mutateAsync(args, options);
      if (isSuccess(resp.status)) {
        return {
          status: 'success',
          data: resp.data,
        };
      } else {
        return {
          status: 'error',
          errors: {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            ...((resp.data ?? {}) as CommonError),
          },
        };
      }
    },
    [_mutateAsync]
  );

  return { mutateAsync, isPending };
};

export const useGetApPayeesBulkInvitationsAvailabilities = () => {
  const getApPayeesBulkInvitationsAvailabilities =
    useGetApPayeesBulkInvitationsAvailabilityHook();

  const handleGetApPayeesBulkInvitationsAvailabilities = useCallback(
    async (apPayeeIds: string[]) => {
      try {
        const resp = await getApPayeesBulkInvitationsAvailabilities({
          'ap_payee_with_relation_search_form[ids][]': apPayeeIds,
        });
        if (isSuccess(resp.status)) {
          return {
            status: 'success',
            data: resp.data,
          };
        }
      } catch (error) {
        return {
          status: 'error',
          error: '',
        };
      }
    },
    [getApPayeesBulkInvitationsAvailabilities]
  );

  return { handleGetApPayeesBulkInvitationsAvailabilities };
};
